import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import { useTranslation } from 'react-i18next';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { fetchProjectWorkflowFlows } from '../../Utils/actionsApi';

const parseFlowId = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const ActionsTriggerFlow = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  workflowFlows = [],
  currentFlowId = null
}) => {
  const { t } = useTranslation();
  const [localFlows, setLocalFlows] = useState([]);
  const [targetFlowId, setTargetFlowId] = useState('');

  const sourceFlowId = useMemo(() => parseFlowId(currentFlowId), [currentFlowId]);

  useEffect(() => {
    if (!caseid) {
      return;
    }
    fetchProjectWorkflowFlows(caseid)
      .then((payload) => {
        setLocalFlows(Array.isArray(payload?.flows) ? payload.flows : []);
      })
      .catch((error) => {
        console.error('Failed to load workflow flows:', error);
        setLocalFlows([]);
      });
  }, [caseid]);

  const selectableFlows = useMemo(() => {
    const source = Array.isArray(workflowFlows) && workflowFlows.length > 0 ? workflowFlows : localFlows;
    return source
      .map((flow) => {
        const id = parseFlowId(flow?.id);
        if (!id) {
          return null;
        }
        return {
          id,
          name: String(flow?.name || `Flow ${id}`),
          orderIndex: Number(flow?.orderIndex || 0)
        };
      })
      .filter((flow) => flow && flow.id !== sourceFlowId)
      .sort((a, b) => {
        if (a.orderIndex !== b.orderIndex) {
          return a.orderIndex - b.orderIndex;
        }
        return a.id - b.id;
      });
  }, [workflowFlows, localFlows, sourceFlowId]);

  useEffect(() => {
    if (parseFlowId(targetFlowId)) {
      return;
    }
    if (selectableFlows.length > 0) {
      setTargetFlowId(String(selectableFlows[0].id));
    }
  }, [targetFlowId, selectableFlows]);

  const selectedTargetFlowId = parseFlowId(targetFlowId);
  const selectedTargetExists = selectableFlows.some((flow) => flow.id === selectedTargetFlowId);
  const isTargetMissing = !selectedTargetFlowId || !selectedTargetExists;

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'triggerflow',
    onSaveStatusChange,
    customComparison: (initial) => {
      const initialTarget = parseFlowId(initial.targetFlowId || initial.target_flow_id);
      const currentTarget = parseFlowId(targetFlowId);
      return initialTarget === currentTarget;
    },
    customSaveData: () => ({
      targetFlowId: parseFlowId(targetFlowId),
      useConditions: true,
      conditions
    }),
    loadDataFunction: (data) => {
      const savedTarget = parseFlowId(
        data?.customData?.targetFlowId || data?.customData?.target_flow_id
      );
      setTargetFlowId(savedTarget ? String(savedTarget) : '');
    }
  });

  return (
    <ActionSettingsLayout
      title={t('workflow.triggerflow', 'Trigger Flow')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled || isTargetMissing}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Card
        sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #5757b010',
          overflow: 'hidden'
        }}
      >
        <CardHeader
          title={
            <Typography
              sx={{
                fontWeight: 700,
                color: '#444',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Box
                component="span"
                sx={{
                  bgcolor: '#5757b0',
                  color: 'white',
                  borderRadius: '6px',
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.7 },
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)'
                }}
              >
                <AltRouteIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                FLOW
              </Box>
              {t('workflow.triggerflow.settings_title', 'Trigger Target')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 }
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Typography sx={{ mb: 2, fontSize: '0.9rem', color: '#666' }}>
            {t(
              'workflow.triggerflow.description',
              'Trigger another flow execution from this flow'
            )}
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel id="triggerflow-target-label">
              {t('workflow.triggerflow.target_flow', 'Target Flow')}
            </InputLabel>
            <Select
              labelId="triggerflow-target-label"
              value={selectedTargetExists ? String(selectedTargetFlowId) : ''}
              label={t('workflow.triggerflow.target_flow', 'Target Flow')}
              onChange={(event) => setTargetFlowId(event.target.value)}
            >
              <MenuItem value="" disabled>
                {t('workflow.triggerflow.select_target_placeholder', 'Select a flow')}
              </MenuItem>
              {selectableFlows.map((flow) => (
                <MenuItem key={flow.id} value={String(flow.id)}>
                  {flow.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography sx={{ fontSize: '0.75rem', color: '#666', mt: 1.25, fontStyle: 'italic' }}>
            {t(
              'workflow.triggerflow.target_flow_help',
              'When this action runs, it schedules the selected flow to start asynchronously.'
            )}
          </Typography>

          {selectableFlows.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t(
                'workflow.triggerflow.no_available_flows',
                'No other flow is available. Create another flow first.'
              )}
            </Alert>
          )}

          {isTargetMissing && selectableFlows.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t(
                'workflow.triggerflow.missing_target_warning',
                'Select a target flow before saving.'
              )}
            </Alert>
          )}
        </CardContent>
      </Card>
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsTriggerFlow);
